// src/pages/Cloud/components/fsc/FSCComplianceDetailPanel.js
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ComplianceDetailTable from '../../charts/ComplianceDetailTable';

import { fetchComplianceDetail } from '../../../../store/slices/complianceSlice';

import { normalizeFscCloud } from '../../../../api/fscCloudContext';

const FSC_STANDARD_KEY = 'fsc';
const FSC_STANDARD_TITLE = '金管會雲端資安基準';
const FSC_ASSESSMENT_ID = 'manual_fsc';

function toMonthState(rawStatus) {
  if (rawStatus === 'passed') return 'Passed';
  if (rawStatus === 'failed') return 'Failed';
  if (rawStatus === 'partial') return 'Partial';
  return 'Skipped';
}

function adaptFscTreeToDetailTableTree({ tree, assessments }) {
  // 新 payload 以 control_uuid 對應；只有真的缺 UUID 的舊 payload 才 fallback control_id。
  // 同一 control_id 可能同時存在 scoped 與 legacy control，不能把 scoped 狀態套到 legacy 節點。
  const byControlUuid = new Map();
  const legacyByControlId = new Map();
  for (const a of assessments || []) {
    const controlUuid = String(a?.control_uuid || '').trim();
    const controlId = String(a?.control_id || '').trim();
    const bucket = controlUuid ? byControlUuid : legacyByControlId;
    const key = controlUuid || controlId;
    if (!key) continue;
    if (!bucket.has(key)) bucket.set(key, []);
    bucket.get(key).push(a);
  }

  const mapNode = (n) => {
    const controlId = n?.control_id;
    const controlUuid = String(n?.uuid || n?.id || '').trim();
    const isAssessable = Boolean(n?.is_assessable);

    const node = {
      // 重要：用來在 table 內判斷「是否 FSC 條目」
      standard_key: FSC_STANDARD_KEY,

      // 保留 id 給既有元件索引邏輯（值改為 UUID）
      id: n?.uuid ?? null,
      // UUID（用於編輯/刪除）
      uuid: n?.uuid ?? null,
      control_id: controlId,
      control_name: n?.control_name || '',
      is_assessable: isAssessable,
      is_legacy_unscoped: Boolean(n?.is_legacy_unscoped),
      read_only: Boolean(n?.read_only),

      children: Array.isArray(n?.children) ? n.children.map(mapNode) : [],

      // 只要 is_assessable=true 就顯示每月狀態列（FSC 的「評估」其實是 control 自身）
      has_assessments: isAssessable,
      assessments: isAssessable
        ? [
            {
              // 固定值
              assessment_id: FSC_ASSESSMENT_ID,
              assessment_name: '',
              monthly_status: {}, // key: YYYY-MM
            },
          ]
        : [],
    };

    // 僅 assessable 節點需要填入 monthly_status（key 用 assessment_month: "YYYY-MM"）
    if (!isAssessable) return node;

    const list = [
      ...((controlUuid && byControlUuid.get(controlUuid)) || []),
      ...((controlId && legacyByControlId.get(String(controlId))) || []),
    ];
    const assessmentRow = node.assessments[0];

    for (const item of list) {
      const monthKey = item?.assessment_month; // "YYYY-MM"
      if (!monthKey) continue;

      const rawStatus = String(item?.status || '').toLowerCase(); // passed / failed / partial
      const state = toMonthState(rawStatus);

      assessmentRow.monthly_status[monthKey] = {
        state,

        // 計數（partial 視為未通過，因此 passed=0）
        passed: state === 'Passed' ? 1 : 0,
        failed: state === 'Failed' || state === 'Partial' ? 1 : 0,
        skipped: state === 'Skipped' ? 1 : 0,
        total: 1,

        // FSC 手動欄位
        source: 'manual',
        raw_status: rawStatus,
        status_display: item?.status_display || '',
        recommendation: item?.recommendation || '',
        remediation_steps: item?.remediation_steps || '',
        assessment_date: item?.assessment_date || '',
        notes: item?.notes || '',
      };
    }

    return node;
  };

  return { controls: (tree || []).map(mapNode) };
}

/** 讓子孫節點也帶 standard_key，供表格展開 key 區分 CIS／FSC */
function stampStandardKey(nodes, standardKey) {
  if (!Array.isArray(nodes)) return [];
  return nodes.map((n) => ({
    ...n,
    standard_key: n?.standard_key || standardKey,
    children: stampStandardKey(n?.children, standardKey),
  }));
}

function mergeCisAndFsc({ cisDetail, fscTree }) {
  const cisDisplayName = String(cisDetail?.standardName ?? '').trim() || 'CIS';

  const cisNode = {
    standard_key: 'cis',
    control_id: 'CIS',
    control_name: cisDisplayName,
    children: stampStandardKey(cisDetail?.controls || [], 'cis'),
    has_assessments: false,
    assessments: [],
  };

  const fscNode = {
    standard_key: FSC_STANDARD_KEY,
    control_id: 'FSC',
    control_name: FSC_STANDARD_TITLE,
    children: stampStandardKey(fscTree?.controls || [], FSC_STANDARD_KEY),
    has_assessments: false,
    assessments: [],
  };

  return { controls: [cisNode, fscNode] };
}

export default function FSCComplianceDetailPanel({
  projectUuid,
  compliancePlatform,
  assessmentResources,
  resourcesLoading,
  onLoadResources,
  canEditFsc,
  cloudScope,
}) {
  const dispatch = useDispatch();

  const {
    complianceDetail: cisComplianceDetail,
    detailLoading,
    selectedCloudScope,
    selectedSecondaryScope,
  } = useSelector((state) => state.compliance);

  const {
    controlTree: fscControlTree,
    assessments: fscAssessments,
    controlsLoading,
    assessmentsLoading,
  } = useSelector((state) => state.fscCompliance);

  const fscCloud = useMemo(() => normalizeFscCloud(compliancePlatform) || 'azure', [compliancePlatform]);
  const effectiveCloudScope = cloudScope || selectedCloudScope;

  useEffect(() => {
    dispatch(fetchComplianceDetail({ standardId: 'cis', platform: fscCloud }));
  }, [dispatch, projectUuid, fscCloud, effectiveCloudScope, selectedSecondaryScope]);

  const fscTree = useMemo(
    () =>
      adaptFscTreeToDetailTableTree({
        tree: fscControlTree,
        assessments: fscAssessments,
      }),
    [fscControlTree, fscAssessments]
  );

  const merged = useMemo(() => mergeCisAndFsc({ cisDetail: cisComplianceDetail, fscTree }), [cisComplianceDetail, fscTree]);

  const loading = detailLoading || controlsLoading || assessmentsLoading;

  return (
    <ComplianceDetailTable
      controls={merged.controls}
      assessmentResources={assessmentResources}
      loading={loading}
      resourcesLoading={resourcesLoading}
      onLoadResources={onLoadResources}
      projectUuid={projectUuid}
      variant="fsc"
      months={cisComplianceDetail?.months || []}
      currentMonthKey={cisComplianceDetail?.currentMonthKey || null}
      canEditFsc={canEditFsc}
      compliancePlatform={fscCloud}
      cloudScope={effectiveCloudScope}
    />
  );
}
